// AddContract.tsx
import React from 'react';
import { Card, CardBody } from '@components/card/card';
import { useFormik } from 'formik';
import { useState } from 'react';
import { Alert, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useAppDispatch } from '@store/hooks';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import { addContract, updateContract } from '@store/HR/Person/contractsSlice';
import useLookupPerContractType from '@hooks/Lookups/use-Lookup-PerContractType';

interface ContractType {
  id?: string;
  personId: string;
  startDate: string;
  endDate: string;
  contractNumber: string;
  contractType: string;
  contractStatus: string;
  description: string;
  signedDate: string;
  signedBy: string;
  annualLeave: number;
  approvedFlag: boolean;
  isAme: boolean;
}

interface Props {
  personId: string;
  contract?: ContractType;
  onSuccess: () => void;
  onCancel: () => void;
  isEdit?: boolean;
}

const ContractType: React.FC<Props> = ({ personId, contract, onSuccess, onCancel, isEdit = false }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const { OptionsPerContractType } = useLookupPerContractType();

  console.log(contract);
  

  const formik = useFormik<ContractType>({
    initialValues: contract || {
      personId: personId,
      startDate: '',
      endDate: '',
      contractNumber: '',
      contractType: '',
      contractStatus: 'active',
      description: '',
      signedDate: '',
      signedBy: '',
      annualLeave: 21,
      approvedFlag: false,
      isAme: false
    },

    validationSchema: yup.object({
      contractNumber: yup.string().required('رقم العقد مطلوب'),
      contractType: yup.string().required('نوع العقد مطلوب'),
      startDate: yup.date().required('تاريخ البداية مطلوب'),
      endDate: yup
        .date()
        .required('تاريخ النهاية مطلوب')
        .min(yup.ref('startDate'), 'يجب أن يكون تاريخ النهاية بعد تاريخ البداية'),
      signedDate: yup
        .date()
        .required('تاريخ التوقيع مطلوب')
        .max(yup.ref('startDate'), 'يجب أن يكون تاريخ التوقيع قبل أو يساوي تاريخ البداية'),
      signedBy: yup.string().required('اسم الموقع مطلوب'),
      annualLeave: yup
        .number()
        .required('عدد أيام الإجازة السنوية مطلوب')
        .min(0, 'يجب أن تكون القيمة موجبة')
        .max(365, 'لا يمكن أن تتجاوز القيمة 365 يوم'),
      description: yup.string().max(500, 'لا يمكن أن يتجاوز الوصف 500 حرف')
    }),

    onSubmit: async (values, { setSubmitting }) => {
      try {
        let response;

        if (isEdit && values.id) {
          response = await dispatch(updateContract(values as any)).unwrap();
        } else {
          response = await dispatch(addContract(values)).unwrap();
        }

        if (response?.succeeded === true) {
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess(false);
            onSuccess();
          }, 2000);
        } else {
          CustomAlert({
            action: 'Error',
            msg: response?.message || 'حدث خطأ أثناء الحفظ'
          });
        }
      } catch (error) {
        console.error('Save error:', error);
        CustomAlert({
          action: 'Error',
          msg: 'حدث خطأ أثناء الحفظ'
        });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    formik.setFieldValue('startDate', value);

    // إذا كان تاريخ التوقيع فارغاً، تعيينه بنفس تاريخ البداية
    if (!formik.values.signedDate) {
      formik.setFieldValue('signedDate', value);
    }
  };

  return (
    <div className='p-3'>
      {/* رسالة النجاح */}
      {showSuccess && (
        <Alert variant='success' className='d-flex align-items-center mb-4'>
          <i className='fa fa-check-circle fa-lg me-3 text-success'></i>
          <div>
            <h6 className='mb-1'>تم بنجاح</h6>
            <p className='mb-0'>{isEdit ? 'تم تحديث العقد بنجاح' : 'تم إضافة العقد بنجاح'}</p>
          </div>
        </Alert>
      )}

      <Form>
        <div className='row mb-3'>
          <div className='col-md-6'>
            <div className='form-group mb-3'>
              <label className='form-label required'>رقم العقد</label>
              <Form.Control
                type='text'
                className='form-control'
                name='contractNumber'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.contractNumber}
                isInvalid={Boolean(formik.touched.contractNumber && formik.errors.contractNumber)}
                placeholder='أدخل رقم العقد'
              />
              <Form.Control.Feedback type='invalid'>{formik.errors.contractNumber}</Form.Control.Feedback>
            </div>
          </div>

          <div className='col-md-6'>
            <div className='form-group mb-3'>
              <label className='form-label required'>نوع العقد</label>
              <Form.Select
                className='form-select'
                name='contractType'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.contractType}
                isInvalid={Boolean(formik.touched.contractType && formik.errors.contractType)}
              >
                <option value=''>اختر نوع العقد</option>
                {OptionsPerContractType.map((m: any) => {
                  return <option value={m.value}>{m.label}</option>;
                })}
              </Form.Select>
              <Form.Control.Feedback type='invalid'>{formik.errors.contractType}</Form.Control.Feedback>
            </div>
          </div>
        </div>

        <div className='row mb-3'>
          <div className='col-md-4'>
            <div className='form-group mb-3'>
              <label className='form-label required'>تاريخ البداية</label>
              <Form.Control
                type='date'
                className='form-control'
                name='startDate'
                onChange={handleStartDateChange}
                onBlur={formik.handleBlur}
                value={formik.values.startDate}
                isInvalid={Boolean(formik.touched.startDate && formik.errors.startDate)}
              />
              <Form.Control.Feedback type='invalid'>{formik.errors.startDate}</Form.Control.Feedback>
            </div>
          </div>

          <div className='col-md-4'>
            <div className='form-group mb-3'>
              <label className='form-label required'>تاريخ النهاية</label>
              <Form.Control
                type='date'
                className='form-control'
                name='endDate'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.endDate}
                isInvalid={Boolean(formik.touched.endDate && formik.errors.endDate)}
                min={formik.values.startDate}
              />
              <Form.Control.Feedback type='invalid'>{formik.errors.endDate}</Form.Control.Feedback>
            </div>
          </div>

          <div className='col-md-4'>
            <div className='form-group mb-3'>
              <label className='form-label required'>تاريخ التوقيع</label>
              <Form.Control
                type='date'
                className='form-control'
                name='signedDate'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.signedDate}
                isInvalid={Boolean(formik.touched.signedDate && formik.errors.signedDate)}
                max={formik.values.startDate}
              />
              <Form.Control.Feedback type='invalid'>{formik.errors.signedDate}</Form.Control.Feedback>
            </div>
          </div>
        </div>

        <div className='row mb-3'>
          <div className='col-md-6'>
            <div className='form-group mb-3'>
              <label className='form-label required'>اسم الموقع</label>
              <Form.Control
                type='text'
                className='form-control'
                name='signedBy'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.signedBy}
                isInvalid={Boolean(formik.touched.signedBy && formik.errors.signedBy)}
                placeholder='أدخل اسم الموقع'
              />
              <Form.Control.Feedback type='invalid'>{formik.errors.signedBy}</Form.Control.Feedback>
            </div>
          </div>

          <div className='col-md-6'>
            <div className='form-group mb-3'>
              <label className='form-label required'>الإجازة السنوية (أيام)</label>
              <Form.Control
                type='number'
                className='form-control'
                name='annualLeave'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.annualLeave}
                isInvalid={Boolean(formik.touched.annualLeave && formik.errors.annualLeave)}
                min={0}
                max={365}
              />
              <Form.Control.Feedback type='invalid'>{formik.errors.annualLeave}</Form.Control.Feedback>
            </div>
          </div>
        </div>

        <div className='row mb-3'>
          <div className='col-md-6'>
            <div className='form-group mb-3'>
              <label className='form-label required'>حالة العقد</label>
              <Form.Select
                className='form-select'
                name='contractStatus'
                onChange={formik.handleChange}
                value={formik.values.contractStatus}
              >
                <option value='active'>نشط</option>
                <option value='expired'>منتهي</option>
                <option value='terminated'>ملغي</option>
                <option value='suspended'>موقوف</option>
              </Form.Select>
            </div>
          </div>

          <div className='col-md-6'>
            <div className='form-group mb-3'>
              <label className='form-label'>الحالة</label>
              <div className='form-check form-switch mt-2'>
                <Form.Check
                  type='switch'
                  id='approvedFlag'
                  name='approvedFlag'
                  checked={formik.values.approvedFlag}
                  onChange={formik.handleChange}
                  label={formik.values.approvedFlag ? 'معتمد' : 'غير معتمد'}
                />
              </div>
            </div>
          </div>
        </div>

        <div className='row mb-4'>
          <div className='col-12'>
            <div className='form-group mb-3'>
              <label className='form-label'>
                الوصف
                <small className='text-muted ms-1'>({formik.values.description?.length || 0}/500)</small>
              </label>
              <Form.Control
                as='textarea'
                rows={3}
                className='form-control'
                name='description'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
                isInvalid={Boolean(formik.touched.description && formik.errors.description)}
                placeholder='أدخل وصفاً للعقد...'
                maxLength={500}
              />
              <Form.Control.Feedback type='invalid'>{formik.errors.description}</Form.Control.Feedback>
            </div>
          </div>
        </div>

        {/* ملخص العقد */}
        <Card className='border-theme bg-theme bg-opacity-10 mb-4'>
          <CardBody className='py-3'>
            <h6 className='text-theme mb-3'>
              <i className='fa fa-info-circle me-2'></i>
              ملخص العقد
            </h6>
            <div className='row small'>
              <div className='col-4'>
                <strong>المدة:</strong>{' '}
                {formik.values.startDate && formik.values.endDate
                  ? `${Math.ceil(
                      (new Date(formik.values.endDate).getTime() - new Date(formik.values.startDate).getTime()) /
                        (1000 * 60 * 60 * 24)
                    )} يوم`
                  : 'غير محسوبة'}
              </div>
              <div className='col-4'>
                <strong>الإجازة السنوية:</strong> {formik.values.annualLeave} يوم
              </div>
              <div className='col-4'>
                <strong>الحالة:</strong>
                <span
                  className={`badge ${formik.values.contractStatus === 'active' ? 'bg-success' : 'bg-warning'} ms-1`}
                >
                  {formik.values.contractStatus === 'active'
                    ? 'نشط'
                    : formik.values.contractStatus === 'expired'
                    ? 'منتهي'
                    : formik.values.contractStatus === 'terminated'
                    ? 'ملغي'
                    : 'موقوف'}
                </span>
              </div>
            </div>
          </CardBody>
        </Card>

        <div className='text-center border-top pt-3'>
          <button
            disabled={formik.isSubmitting || !formik.isValid}
            type='button'
            className='btn btn-theme btn-lg px-5 me-3 rounded-2'
            onClick={() => formik.submitForm()}
          >
            {formik.isSubmitting ? (
              <>
                <i className='fa fa-spinner fa-spin fa-fw me-2'></i>
                جاري المعالجة...
              </>
            ) : (
              <>
                <i className={`fa fa-fw me-2 ${isEdit ? 'fa-edit' : 'fa-plus-circle'}`}></i>
                {isEdit ? 'تحديث العقد' : 'إضافة العقد'}
              </>
            )}
          </button>

          <button
            type='button'
            className='btn btn-outline-secondary btn-lg px-5 rounded-2'
            onClick={onCancel}
            disabled={formik.isSubmitting}
          >
            <i className='fa fa-times fa-fw me-2'></i>
            إلغاء
          </button>
        </div>
      </Form>
    </div>
  );
};

export default ContractType;
